import { cloneDeep } from "lodash";
import { EditClientDataShippingAddress } from "../../../../types/Client";
import { ProductEditData } from "../../../../types/Product";
import { hasRowContent } from "./orderProductTree";
import { QuoteEditData } from "../../../../types/Quote";

export function getAddressInfo(
  clientAddress: EditClientDataShippingAddress | null,
  typeAddress: EditClientDataShippingAddress["typeAddress"]
) {
  if (!clientAddress) {
    return null;
  }

  const isEmptyAddress = (address: EditClientDataShippingAddress) =>
    Object.entries(address).every(
      ([key, val]) => key === "countryId" || key === "typeAddress" || !val
    );

  if (!isEmptyAddress(clientAddress)) {
    return {
      ...clientAddress,
      typeAddress,
    };
  }

  return null;
}

export function editQuoteOrderSetObjForSending(data: QuoteEditData["order"]) {
  const { clientCreditCardId } = data;
  const currentData = {
    ...data,
    clientCreditCardId: clientCreditCardId ? clientCreditCardId : null,
  };

  return currentData;
}

export function editQuoteSetObjForSending(data: QuoteEditData) {
  const {
    id,
    client,
    status,
    leadSourceStatus,
    leadSource,
    shippingTax,
    withoutTax,
    useAdditionalLine,
    userId,
    note,
    withoutTotal,
    reasonLost,
    clientFlag,
    priority,
    closeProbability,
    waitingOn,
    order,
    orderProducts,
    useInventory,
    requiredDeliveryDate,
    followUpDate,
    supplierId,
    quoteAdditionalRows,
    shippedEmailTplId,
    domain,
  } = data;

  const compactObject = (data: any) => {
    if (typeof data !== "object") {
      return data;
    }

    return Object?.keys(data)?.reduce(function (accumulator, key) {
      const isObject = typeof data[key] === "object";
      const value: any = isObject ? compactObject(data[key]) : data[key];
      const isEmptyObject = isObject && !Object.keys(value).length;
      if (value === undefined || isEmptyObject) {
        return accumulator;
      }

      return Object.assign(accumulator, { [key]: value });
    }, {});
  };

  /*const getAddresses = (clientShippingAddresses: EditClientDataShippingAddress, clientBillingAddresses: EditClientDataShippingAddress) => {
    const addressesArray:Partial<EditClientDataShippingAddress>[] = []
    const isEmptyAddress = (address: EditClientDataShippingAddress) =>  Object.entries(address).every(([key, val]) => key === 'countryId' || key === 'typeAddress' || !val)
    const isEmptyClientShippingAddresses = !clientShippingAddresses.id && isEmptyAddress(clientShippingAddresses)
    const isEmptyClientBillingAddresses = !clientBillingAddresses.id && isEmptyAddress(clientBillingAddresses)

    if (!isEmptyClientShippingAddresses) {
      addressesArray.push(getClientAddress(clientShippingAddresses))
    }

    if (!isEmptyClientBillingAddresses) {
      addressesArray.push(getClientAddress(clientBillingAddresses))
    }

    return addressesArray
  }*/

  const dataForUpdate = {
    id,
    leadSource,
    userId,
    withoutTotal,
    withoutTax,
    reasonLost: reasonLost || null,
    clientFlag: clientFlag || null,
    priority: priority || null,
    closeProbability: closeProbability || null,
    waitingOn: waitingOn || null,
    useAdditionalLine,
    status,
    shippingTax,
    note,
    leadSourceStatus,
    shippedEmailTplId,
    domain,
    client: {
      accessLevelCd: client.accessLevelCd,
      accountName: client.accountName,
      clientContact: client.clientContacts,
      shippingAddress: getAddressInfo(
        client.clientShippingAddresses,
        "Shipping"
      ),
      billingAddress: getAddressInfo(client.clientBillingAddresses, "Billing"),
      company: client.company,
      id: client.id,
      industryTag: client.industryTag ? client.industryTag : 1,
      userId: client.userId,
      webAdress: client.webAddress,
    },
    orderProducts: buildOrderProductsForSending(orderProducts, !!id),
    additionalQuoteLine: quoteAdditionalRows
      ? quoteAdditionalRows.map((el) => ({
          id: el.id || undefined,
          description: el.description,
        }))
      : undefined,
    useInventory: useInventory || false,
    requiredDeliveryDate,
    followUpDate,
    supplierId: supplierId || null,
    order: editQuoteOrderSetObjForSending(order),
  };

  return dataForUpdate;
}

// Keys that do not make a blank row count as filled in (UI-only or identity keys).
const ROW_KEYS_IGNORED_FOR_EMPTINESS = [
  "id",
  "isNewProduct",
  "key",
  "parentKey",
  "index",
];

const getProductNumber = (count: string | null | number) => {
  if (typeof count === "number") {
    return count;
  }

  if (typeof count === "string" && !isNaN(+count)) {
    return +count;
  }

  return null;
};

/**
 * Quote lines for the createQuote/updateQuote mutations.
 * Trailing blank rows are trimmed first, then every remaining row gets its parent link:
 * - parentOrderProductId: the saved parent's id, or null;
 * - parentIndex: the parent's position in the sent list when the parent is a new row, or null.
 * Both keys are always sent on update (an explicit null clears the parent on the server).
 * For a new quote every row is new, so only parentIndex is sent.
 */
export function buildOrderProductsForSending(
  orderProductsArr: ProductEditData[],
  isExistingQuote: boolean
) {
  // A service row added with "+ service" and left blank would be saved as an empty
  // line under its parent, so it is dropped.
  const source = orderProductsArr.filter(
    (el) => !(el.parentKey && !el.id && !hasRowContent(el))
  );
  const reverseOrderProducts = cloneDeep(source).reverse();
  const removeEmptyProduct: ProductEditData[] = [];
  let isNeedDelete = true;

  const isEmptyObj = (productVal: ProductEditData) =>
    Object.entries(productVal).every(
      ([key, val]) => ROW_KEYS_IGNORED_FOR_EMPTINESS.includes(key) || !val
    );

  reverseOrderProducts.reduce((accumulator, currentValue, index) => {
    if (isNeedDelete) {
      isNeedDelete =
        isEmptyObj(currentValue) &&
        reverseOrderProducts[index + 1] &&
        isEmptyObj(reverseOrderProducts[index + 1]);
    }

    if (isNeedDelete) {
      return accumulator;
    }
    accumulator.push(currentValue);
    return accumulator;
  }, removeEmptyProduct);

  if (removeEmptyProduct.length && isEmptyObj(removeEmptyProduct[0])) {
    removeEmptyProduct.splice(0, 1);
  }

  const trimmed = cloneDeep(removeEmptyProduct).reverse();
  const indexByKey = new Map<string, number>();
  trimmed.forEach((el, index) => {
    if (el.key) {
      indexByKey.set(el.key, index);
    }
  });

  return trimmed.map((el) => {
    const trimmedDescription = el.description || "";
    const parentPosition =
      el.parentKey && el.parentKey !== el.key
        ? indexByKey.get(el.parentKey)
        : undefined;
    const parent =
      parentPosition !== undefined ? trimmed[parentPosition] : undefined;
    // One level only: a parent that is itself a child is ignored.
    const validParent = parent && !parent.parentKey ? parent : undefined;
    const parentLink = {
      parentIndex:
        validParent && !validParent.id ? (parentPosition as number) : null,
      ...(isExistingQuote
        ? { parentOrderProductId: validParent?.id ?? null }
        : {}),
    };

    return {
      id: el.id,
      description: trimmedDescription || "",
      priceTableId: el.priceTableId || null,
      productId: el.productId || null,
      productOptionString: `${el.productOptionString}` || null,
      quantity: getProductNumber(el.quantity),
      unitPrice: getProductNumber(el.unitPrice),
      isForSupplier: el.isForSupplier ?? true,
      recordType: el.recordType ?? null,
      ...parentLink,
    };
  });
}
